/**
 * 4401 'revoked' — the server closed this socket because its user's credentials were revoked
 * (password reset, "log out all devices", role change, retirement, account deletion). See
 * server/lib/authRevocationBus.ts. Security review 2026-10-06.
 *
 * Reconnecting with the same token cannot succeed (the upgrade refuses it), so a hook stops and
 * reports the token exactly like an HTTP 401 (lib/sessionWatch): AuthProvider re-checks the
 * session — profile → one refresh → sign-out — and the person lands on sign-in instead of a
 * console silently retrying forever.
 */
import { reportUnauthorizedToken } from './sessionWatch';

export const WS_AUTH_REVOKED_CLOSE_CODE = 4401;

/**
 * Call from onclose. True = do NOT reconnect (and the token has been reported). A newer token in
 * storage (signed in again after the revocation) may reconnect: it was minted after the stamp.
 */
export function stopAfterRevokedClose(code: number, tokenSent: string | null | undefined, currentToken: string | null | undefined): boolean {
  if (code !== WS_AUTH_REVOKED_CLOSE_CODE) return false;
  if (currentToken && tokenSent && currentToken !== tokenSent) return false;
  if (tokenSent) {
    try { reportUnauthorizedToken(tokenSent); } catch { /* reporting must never break onclose */ }
  }
  return true;
}

/**
 * How long a hook waits before its ONE retry after a 4401 — long enough for AuthProvider's
 * re-check (profile → one refresh → sign-out) to finish first.
 */
export const WS_REVOKED_RETRY_DELAY_MS = 5000;

export type RevokedCloseAction = 'proceed' | 'retry-once' | 'stop';

/**
 * Call from onclose instead of stopAfterRevokedClose (review 2026-10-06).
 *
 * The server stamps a revocation with the time AFTER the write committed, so a socket opened by
 * a fresh sign-in inside [stamp, publish] — milliseconds — can be closed with 4401 too. Stopping
 * for good would leave a still-signed-in person with a dead console until a reload. So:
 *  - 'proceed'    not ours (another code, or a newer token is in storage): normal reconnect logic;
 *  - 'retry-once' token reported (AuthProvider re-checks); reconnect ONCE after
 *                 WS_REVOKED_RETRY_DELAY_MS, and only if a token is still in storage then. A
 *                 really-revoked token is refused at upgrade, and the sign-out removes it anyway;
 *  - 'stop'       the retry was already spent, or there is no token left: final.
 * `alreadyRetried` is the hook's flag; it sets it on 'retry-once' and clears it when a socket opens.
 */
export function afterRevokedClose(
  code: number,
  tokenSent: string | null | undefined,
  currentToken: string | null | undefined,
  alreadyRetried: boolean,
): RevokedCloseAction {
  if (!stopAfterRevokedClose(code, tokenSent, currentToken)) return 'proceed';
  if (alreadyRetried || !currentToken) return 'stop';
  return 'retry-once';
}
